import { useEffect, useState } from "react";
import { Button, Popover, PopoverContent, PopoverHandler } from "@material-tailwind/react";
import {
  Card,
  Typography
} from "@material-tailwind/react";
import moment from "moment";
import { get } from "lodash";
import useWindowWidth from "utills/InitWidthDevice";
import { useParse, useTranslation } from "@refinedev/core";
import { Breadcrumbs } from "components/breadcrumbs";
import ApiService from "utills/ApiService";
import { DotsHorizontalIcon, MailIcon, SearchIcon, UserIcon } from "@heroicons/react/outline";
import ConvertCurrency from "utills/convertCurrency";
import Pagination from "components/wps/Pagination";
import { FullScreenLoading } from "components/fullScreenLoading";
import toast from "react-hot-toast";
import NewButton from "components/button";
import { convertCurrency, isHaveActionPermission } from "utills/helpers";

const OrderForm = (props: any) => {
  const { translate, isMobile, language, orderID, token } = props;
  const [openAction, setOpenAction] = useState(false);
  const TABLE_HEAD = [translate('no'), translate('ticket-name'), translate('quantity'), translate('price'), translate('discount'), translate('total'), translate('used-code')];
  const [loading, setLoading] = useState(false);
  const [orderDetail, setOrderDetail] = useState(null as any);
  const [page, setPage] = useState(1);
  const [paging, setPaging] = useState(null as any);
  const [attendants, setAttendants] = useState([] as any);
  const [filter, setFilter] = useState(true);
  const [email, setEmail] = useState('');
  const [activeAttendants, setActiveAttendants] = useState(false);
  const [symbolCurrency, setSymbolCurrency] = useState('đ');
  const [currentRates, setCurrentRates] = useState(0);
  const [showPlatform, setShowPlatform] = useState(false);

  useEffect(() => {
    if (orderDetail && orderDetail?.rates && orderDetail?.rates?.rates && orderDetail?.rates?.rates?.VND) {
      setCurrentRates(orderDetail?.rates?.rates?.VND);
    }
  }, [orderDetail]);

  useEffect(() => {
    const wsCurrency = localStorage.getItem('wsCurrency');

    if (wsCurrency && wsCurrency.toLowerCase() === 'usd') {
      setSymbolCurrency('$');
    } else {
      setSymbolCurrency('đ');
    }
  }, []);

  useEffect(() => {
    if (orderID !== '') {
      fetchOrderDetail();
      handleCheckShowPlatform();
    }
  }, [orderID]);

  const fetchOrderDetail = async () => {
    setLoading(true);
    try {
      const result = await ApiService.get(`orders/${orderID}`, token);

      if (result) {
        setOrderDetail(result);
        setFilter(true);
      }
    } catch (error: any) {
      toast.error(error.message);
      setLoading(false);
    }
  }

  const handleCheckShowPlatform = async () => {
    try {
      const result = await ApiService.get(`orders/whitelabel/existed`, token);

      if (result && result?.whitelabel_orders_existed) {
        setShowPlatform(true);
      }


    } catch (error: any) {
      toast.error(error.message);
    }
  }

  useEffect(() => {
    if (filter && orderID !== '') {
      fetchAttendants();
    }
  }, [page, filter, orderID, email]);

  const fetchAttendants = async () => {
    setLoading(true);
    try {
      const result = await ApiService.get(`orders/${orderID}/attendants/page/${page}?limit=12&email=${email}`, token);

      if (result) {
        setAttendants(result.attendants);

        if (result.attendants && result.attendants.length > 0) {
          setActiveAttendants(true);
        }
        setPaging(result.paging);
        setFilter(false);
      }
    } catch (error: any) {
      toast.error(error.message);
    }

    setLoading(false);
  }

  const handleSendSuccessEmail = async () => {
    setLoading(true);
    try {
      const result = await ApiService.post(`orders/send-email/payment-success/orders/${orderID}`, {}, token);

      if (result) {
        toast.success(translate("send-email-successfully"));
      }
    } catch (error: any) {
      toast.error(error.message);
    }

    setLoading(false);
  }

  const handleSendEmailForParticipant = async (participant_id: string) => {
    setLoading(true);
    try {
      const result = await ApiService.post(`orders/send-email/payment-success/orders/${orderID}/participant/${participant_id}`, {}, token);

      if (result) {
        toast.success(translate("send-email-successfully"));
      }
    } catch (error: any) {
      toast.error(error.message);
    }

    setLoading(false);
  }

  const handleRedirectParticipant = (item: any) => {
    if (orderDetail && orderDetail.event && orderDetail.event.event_type && orderDetail.event.event_type === 'attractions') {
      window.location.href = `/attendees/attractions/${item.participant_id}/detail`;
    } else {
      window.location.href = `/attendees/sports/${item.participant_id}/detail`;
    }
  }

  const getPaymentMethod = (method: string) => {
    const payment_method = method.toLowerCase();
    switch (payment_method) {
      case 'atm':
        return translate('atm');
      case 'bank_transfer':
        return translate('bank-transfer');
      case 'credit':
        return translate('credit');
      case 'partner_vnpay':
        return 'Partner VNPay';
      case 'vn_pay_b':
        return 'VNPAY';
      case 'vnpay':
        return 'QR Code';
      case 'zalopay':
        return 'ZaloPay';
      case 'viet_qr':
        return 'Viet QR';
      case 'momo':
        return translate('momo');
      case 'voucher':
        return translate('coupon');
      case 'shipcode':
        return 'Ship Code';
      case 'free':
        return translate('free-ticket');
      case 'bnpl':
        return translate('bnpl');
      case 'viettel_money':
        return translate('viettel_money');
      case 'v_atm':
        return translate('v_atm');
      case 'v_credit':
        return translate('v_credit');
      case 'payoo_atm':
        return translate('payoo_atm');
      case 'payoo_credit':
        return translate('payoo_credit');
      case 'onepay_viet_qr':
        return translate('onepay_viet_qr');
      case 'stripe':
        return translate('stripe-payment');
      default:
        return method;
    }
  }

  const breadcrumbItems = [
    {
      label: translate('orders-title'),
      href: '/orders'
    },
    {
      label: translate('order-detail'),
      href: ''
    },
  ];

  return (
    <div className="order-form">
      <Breadcrumbs type="wps" breadcrumbItems={breadcrumbItems} disableBorder={true} additionalLabel={(
        <>
          {
            !isMobile ?
              <div className="flex items-center justify-end gap-x-[12px]">
                {
                  orderDetail?.order_status === 'dat_hang_thanh_cong' && isHaveActionPermission("", "orders", "edit") &&
                  <a href={`/orders/${orderID}/edit`}>
                    <div className="h-[48px] px-[12px] flex items-center justify-center border-[1px] rounded-[4px] border-[#011BB6] cursor-pointer">
                      <img src="/images/icons/icon-edit-detail.svg" className="mr-[4px]" alt="icon-edit" />

                      <div className="text-[14px] font-[500] text-[#011BB6]">{translate('edit')}</div>
                    </div>
                  </a>
                }

                {
                  (orderDetail && orderDetail.order_status && orderDetail.order_status === 'dat_hang_thanh_cong' && isHaveActionPermission("", "orders", "edit")) &&
                  <Button
                    placeholder="Button resend email"
                    className="h-[48px] border border-brand-500 bg-brand-500 !text-white !text-14 !font-500 px-[16px] flex items-center justify-center gap-x-[4px]"
                    onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                    onResizeCapture={() => { }}
                    onClick={handleSendSuccessEmail}>
                    <MailIcon className="w-4 h-4 icon-email" />

                    <span>{translate('resend-email')}</span>
                  </Button>
                }
              </div>
              :
              <div className="flex items-center">
                {isHaveActionPermission("", "orders", "edit") && (
                  <a href={`/orders/${orderID}/edit`} className="mr-[36px]">
                    <img
                      src="/images/icons/icon-edit-mobile.svg"
                      alt="icon-edit"
                    />
                  </a>
                )}

                {isHaveActionPermission("", "orders", "edit") && (
                  <Popover
                    animate={{
                      mount: { scale: 1, y: 0 },
                      unmount: { scale: 0, y: 25 },
                    }}
                    placement="bottom"
                    open={openAction} handler={() => setOpenAction(!openAction)}
                  >
                    <PopoverHandler>
                      <DotsHorizontalIcon className="w-7 h-7 text-brand-500" />

                    </PopoverHandler>
                    <PopoverContent
                      placeholder="popover btn list action"
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined} onResize={() => { }}
                      onResizeCapture={() => { }}
                      id="popover-list-action"
                      className="w-[calc(100%-16px)] !left-[8px]">
                      {
                        (orderDetail && orderDetail.order_status && orderDetail.order_status === 'dat_hang_thanh_cong') &&
                        <Button
                          placeholder="Button resend email"
                          className="w-full h-[40px] flex items-center justify-center bg-brand-500 !text-white gap-x-[8px] text-14 px-[16px]"
                          onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                          onResizeCapture={() => { }}
                          onClick={handleSendSuccessEmail}>
                          <MailIcon className="w-4 h-4 icon-email" />

                          <span>{translate('resend-email')}</span>
                        </Button>
                      }
                    </PopoverContent>
                  </Popover>
                )}
              </div>
          }
        </>
      )} />

      {
        orderDetail &&
        <div className={`${openAction && "grayscale blur-sm opacity-100"} ${isMobile ? 'w-full px-0 py-[16px] flex flex-col gap-y-[16px] items-center justify-center' : 'p-[24px] w-full grid grid-cols-1 gap-[16px] '}`}>
          {
            orderDetail.buyer &&
            <div className={`${isMobile ? 'w-[calc(100%-32px)]' : 'w-full'} bg-white rounded-[4px]`}>
              <div className={`w-full min-h-[64px] border-b border-[#c5ccd2] flex items-center justify-between ${isMobile ? 'px-[16px]' : 'px-[24px]'}`}>
                <div className="text-16 font-700">{translate('buyer-information')}</div>
              </div>

              <div className={`${isMobile ? 'p-[16px]' : 'p-[24px]'} w-full grid grid-cols-1 gap-[16px]`}>
                <div className={`${isMobile ? 'gap-x-[12px]' : 'justify-between'} w-full flex text-14`}>
                  <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500`}>{translate('full-name')}</div>
                  <div className="flex-1">{orderDetail.buyer.name}</div>
                </div>

                <div className={`${isMobile ? 'gap-x-[12px]' : 'justify-between'} w-full flex text-14`}>
                  <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500`}>Email</div>
                  <div className="flex-1">
                    {orderDetail.buyer.email}

                    {
                      orderDetail.is_sent_email_success &&
                      <span className="text-[#16831c] !font-400 ml-[4px]">({translate('send-email-payment-success')})</span>
                    }
                  </div>
                </div>

                <div className={`${isMobile ? 'gap-x-[12px]' : 'justify-between'} w-full flex text-14`}>
                  <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500`}>{translate('phone')}</div>
                  <div className="flex-1">+{orderDetail.buyer.phone_area_code} {orderDetail.buyer.phone}</div>
                </div>
              </div>
            </div>
          }

          <div className={`${isMobile ? 'w-[calc(100%-32px)]' : 'w-full'} bg-white rounded-[4px]`}>
            <div className={`w-full min-h-[64px] flex items-center justify-between border-b border-[#c5ccd2] ${isMobile ? 'px-[16px]' : 'px-[24px]'}`}>
              <div className="text-16 font-700">{translate('order-information')}</div>
            </div>

            <div className={`w-full grid gap-[16px] ${isMobile ? 'p-[16px] grid-cols-1' : 'p-[24px] grid-cols-2'}`}>
              <div className="w-full flex items-center justify-between gap-x-[12px] text-14">
                <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500`}>{translate('order-code')}</div>
                <div className="flex-1 !text-16 text-[#081774] font-700">{orderDetail.code}</div>
              </div>

              <div className="w-full flex items-center justify-between gap-x-[12px] text-14">
                <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500`}>{translate('time-order')}</div>
                <div className="flex-1">{moment(orderDetail.created_at).format('DD/MM/YYYY HH:mm:ss')}</div>
              </div>

              <div className="w-full flex items-center justify-between gap-x-[12px] text-14">
                <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500`}>{translate('order-amount')}</div>
                <div className="flex-1 !text-16 text-[#081774] font-700">{symbolCurrency} {convertCurrency(orderDetail.grand_total, orderDetail?.currency, currentRates)}</div>
              </div>

              {
                orderDetail.purchased_at && orderDetail.purchased_at !== "1970-01-01 16:00:00" &&
                <div className="w-full flex items-center justify-between gap-x-[12px] text-14">
                  <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500`}>{translate('time-order-success')}</div>
                  <div className="flex-1">{moment(orderDetail.purchased_at).format('DD/MM/YYYY HH:mm:ss')}</div>
                </div>
              }

              <div className="w-full flex items-center justify-between gap-x-[12px] text-14">
                <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500`}>{translate('order-status')}</div>
                <div className="flex-1">
                  {
                    orderDetail.order_status === 'doi_thanh_toan' &&
                    <span className="text-12 rounded-[16px] !font-500 px-[16px] py-[5px] text-center text-[#7E22D7] bg-[#F3E8FF]">{translate('order-pending')}</span>
                  }

                  {
                    orderDetail.order_status === 'dat_hang_thanh_cong' &&
                    <span className="text-12 rounded-[16px] !font-500 px-[16px] py-[5px] text-center text-[#1d813f] bg-[#dcfce7]">{translate('order-success')}</span>
                  }

                  {
                    orderDetail.order_status === 'da_huy' &&
                    <span className="text-12 rounded-[16px] !font-500 px-[16px] py-[5px] text-center text-[#9D0520] bg-[#FFCFDD]">{translate('order-canceled')}</span>
                  }

                  {
                    orderDetail.order_status === 'hoan_tien' &&
                    <span className="text-12 rounded-[16px] !font-500 px-[16px] py-[5px] text-center text-[#A16207] bg-[#FEF9C3]">{translate('order-refund')}</span>
                  }
                </div>
              </div>

              {
                orderDetail.order_status && orderDetail.order_status === 'da_huy' &&
                <div className="w-full flex items-center justify-between gap-x-[12px] text-14">
                  <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500`}>{translate('time-order-fail')}</div>
                  {
                    (orderDetail.expired_payment_at && orderDetail.expired_payment_at !== "1970-01-01 16:00:00") ?
                      <div className="flex-1">{moment(orderDetail.expired_payment_at).format('DD/MM/YYYY HH:mm:ss')}</div>
                      :
                      <div className="flex-1">{moment(orderDetail.canceled_at).format('DD/MM/YYYY HH:mm:ss')}</div>
                  }
                </div>
              }

              {
                orderDetail.transactions && orderDetail.transactions.length > 0 &&
                <div className="w-full flex items-center justify-between gap-x-[12px] text-14">
                  <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500`}>{translate('payment-method')}</div>
                  <div className="flex-1">{getPaymentMethod(get(orderDetail.transactions[0], `payment_method_name_${language}`, ''))}</div>
                </div>
              }
              <div className="w-full flex items-center justify-between gap-x-[12px] text-14">
                <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500`}>{translate('payment-unit')}</div>
                <div className="flex-1 uppercase">{orderDetail?.currency}</div>
              </div>

              {
                orderDetail.event &&
                <div className="w-full flex items-center justify-between gap-x-[12px] text-14">
                  <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500`}>{translate('event-name')}</div>
                  <div className="flex-1">{get(orderDetail.event, `name_${language}`, '')}</div>
                </div>
              }

              {
                orderDetail.event && orderDetail.event.event_type && orderDetail.event.event_type === 'sports' &&
                <div className="w-full flex items-center justify-between gap-x-[12px] text-14">
                  <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500`}>{translate('group-name')}</div>
                  {
                    orderDetail.extra && orderDetail.extra.checkout_data && orderDetail.extra.checkout_data.club_name && orderDetail.extra.checkout_data.club_name !== '' ?
                      <div className="flex-1">{orderDetail.extra.checkout_data.club_name}</div>
                      :
                      <div className="flex-1"></div>
                  }
                </div>
              }

              {
                orderDetail.event && orderDetail.event.event_type && orderDetail.event.event_type === 'attractions' &&
                <div className="w-full flex items-center justify-between gap-x-[12px] text-14">
                  <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500`}>{translate('e-ticket')}</div>
                  {
                    orderDetail.ticket_use_for_date && orderDetail.ticket_use_for_date !== '' ?
                      <div className="flex-1">{moment(orderDetail.ticket_use_for_date).format('DD/MM/YYYY')}</div>
                      :
                      <div className="flex-1"></div>
                  }
                </div>
              }

              {
                showPlatform &&
                <div className="w-full flex items-center justify-between gap-x-[12px] text-14">
                  <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500`}>{translate('ticket_platform')}</div>
                  <div className="flex-1">{(orderDetail.ticket_platform.includes('actiup')) ? translate('actiup_platform') : translate('partner_platform')}</div>
                </div>
              }

              {
                (orderDetail?.auth_identifier) &&
                <div className="w-full flex items-center justify-between gap-x-[12px] text-14">
                  <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500`}>{translate('private_flow')}</div>
                  <div className="flex-1">{orderDetail?.auth_identifier}</div>
                </div>
              }

            </div>
          </div>

          {
            orderDetail.is_vat === "1" &&
            <div className={`${isMobile ? 'w-[calc(100%-32px)]' : 'w-full'} bg-white rounded-[4px]`}>
              <div className={`${isMobile ? 'px-[16px]' : 'px-[24px]'} w-full min-h-[64px] flex items-center justify-between border-b border-[#c5ccd2]`}>
                <div className="text-16 font-700">{translate('invoice-information')}</div>
              </div>

              <div className={`${isMobile ? 'p-[16px] grid grid-cols-1' : 'p-[24px] grid grid-cols-2'} w-full gap-[16px]`}>
                <div className="w-full flex items-center justify-between gap-[12px] text-14">
                  <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500`}>{translate('company-name')}</div>
                  <div className="flex-1">{orderDetail.tax?.company_name}</div>
                </div>

                <div className="w-full flex items-center justify-between gap-[12px] text-14">
                  <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500`}>{translate('recipient-name')}</div>
                  <div className="flex-1">{orderDetail.tax?.recipient_name}</div>
                </div>

                <div className="w-full flex items-center justify-between gap-[12px] text-14">
                  <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500`}>{translate('company-address')}</div>
                  <div className="flex-1">{orderDetail.tax?.company_address}</div>
                </div>

                <div className="w-full flex items-center justify-between gap-[12px] text-14">
                  <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500`}>{translate('recipient-phone')}</div>
                  <div className="flex-1">{orderDetail.tax?.recipient_phone}</div>
                </div>

                <div className="w-full flex items-center justify-between gap-[12px] text-14">
                  <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500`}>{translate('company-tax')}</div>
                  <div className="flex-1">{orderDetail.tax?.company_tax_code}</div>
                </div>

                <div className="w-full flex items-center justify-between gap-[12px] text-14">
                  <div className={`${isMobile ? 'w-[100px]' : 'w-[180px]'} font-500`}>{translate('email')}</div>
                  <div className="flex-1">{orderDetail.tax?.recipient_address}</div>
                </div>
              </div>
            </div>
          }

          {
            orderDetail.items && orderDetail.items.length > 0 &&
            <div className="w-full bg-white rounded-[4px] p-[24px]">
              <div className="grid grid-cols-1 gap-[16px]">
                <div className="text-16 font-700">{translate('purchased-product')}</div>

                {
                  !isMobile &&
                  <div className="purchased__board">
                    <Card
                      onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                      onResizeCapture={() => { }}
                      className="h-full w-full overflow-scroll overflow-y-hidden" placeholder="Card name table">
                      <table className="w-full min-w-max table-auto text-left">
                        <thead className="bg-[#e9ecff]">
                          <tr>
                            {TABLE_HEAD.map((head) => (
                              <th
                                key={head}
                                className="p-4"
                              >
                                <Typography
                                  variant="small"
                                  className="text-14 !font-500"
                                  placeholder="Typography"
                                  onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                                  onResizeCapture={() => { }}
                                >
                                  {head}
                                </Typography>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {
                            orderDetail.items.length > 0 &&
                            orderDetail.items.map((item: any, index: number) => {
                              const isLast = index === orderDetail.items.length - 1;
                              const classes = isLast ? "p-4" : "p-4 border-b border-blue-gray-50";

                              return (
                                <tr key={`event_${item.id}_${index}`}>
                                  <td className={classes}>
                                    <Typography
                                      variant="small"
                                      color="blue-gray"
                                      className="font-normal"
                                      placeholder="Typography"
                                      onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                                      onResizeCapture={() => { }}
                                    >
                                      {index + 1}
                                    </Typography>
                                  </td>
                                  <td className={classes}>
                                    <Typography
                                      variant="small"
                                      color="blue-gray"
                                      className="font-normal order-code"
                                      placeholder="Typography"
                                      onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                                      onResizeCapture={() => { }}
                                    >
                                      {get(item, `product_name_${language}`, '')}
                                    </Typography>
                                  </td>
                                  <td className={classes}>
                                    <Typography
                                      onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                                      onResizeCapture={() => { }}
                                      variant="small"
                                      color="blue-gray"
                                      className="font-normal"
                                      placeholder="Typography"
                                    >
                                      {item.quantity}
                                    </Typography>
                                  </td>
                                  <td className={classes}>
                                    <Typography
                                      onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                                      onResizeCapture={() => { }}
                                      variant="small"
                                      color="blue-gray"
                                      className="font-normal"
                                      placeholder="Typography"
                                    >
                                      {symbolCurrency} {convertCurrency(item.product_price, orderDetail?.currency, currentRates)}
                                    </Typography>
                                  </td>
                                  <td className={classes}>
                                    <Typography
                                      onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                                      onResizeCapture={() => { }}
                                      variant="small"
                                      color="blue-gray"
                                      className="font-normal"
                                      placeholder="Typography"
                                    >
                                      {symbolCurrency} {convertCurrency(item.discount_amount, orderDetail?.currency, currentRates)}
                                    </Typography>
                                  </td>
                                  <td className={classes}>
                                    <Typography
                                      onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                                      onResizeCapture={() => { }}
                                      variant="small"
                                      color="blue-gray"
                                      className="font-normal"
                                      placeholder="Typography"
                                    >
                                      {symbolCurrency} {convertCurrency(item.grand_total, orderDetail?.currency, currentRates)}
                                    </Typography>
                                  </td>
                                  <td className={classes}>

                                    <Typography
                                      onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                                      onResizeCapture={() => { }}
                                      variant="small"
                                      color="blue-gray"
                                      className="font-normal"
                                      placeholder="Typography"
                                    >
                                      {
                                        orderDetail.coupons && orderDetail.coupons.length > 0 &&
                                        orderDetail.coupons.map((coupon: any, index: number) => {
                                          return (
                                            <span key={`coupon_${index}`}>{coupon}</span>
                                          )
                                        })
                                      }
                                    </Typography>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </Card>
                  </div>
                }

              </div>
            </div>
          }

          {
            (isMobile && orderDetail.items && orderDetail.items.length > 0) &&
            <div className="w-[calc(100%-32px)] grid grid-cols-1 gap-[8px]">
              {
                orderDetail.items.map((item: any, index: number) => {
                  return (
                    <div className="w-full bg-white rounded-[8px] p-[16px] grid grid-cols-1 gap-[8px]" key={`purchased__item__${index}`}>
                      <div className="w-full flex gap-[8px]">
                        <div className="w-[100px] font-500 text-14 text-[#262626]">{translate('no')}</div>
                        <div className="text-14 font-400 text-[#262626]">{index + 1}</div>
                      </div>

                      <div className="w-full flex gap-[8px]">
                        <div className="w-[100px] font-500 text-14 text-[#262626]">{translate('ticket-name')}</div>
                        <div className="text-14 font-400 text-[#262626]">{get(item, `product_name_${language}`, '')}</div>
                      </div>

                      <div className="w-full flex gap-[8px]">
                        <div className="w-[100px] font-500 text-14 text-[#262626]">{translate('quantity')}</div>
                        <div className="text-14 font-400 text-[#262626]">{item.quantity}</div>
                      </div>

                      <div className="w-full flex gap-[8px]">
                        <div className="w-[100px] font-500 text-14 text-[#262626]">{translate('price')}</div>
                        <div className="text-14 font-400 text-[#262626]">{symbolCurrency} {convertCurrency(item.product_price, orderDetail?.currency, currentRates)}</div>
                      </div>

                      <div className="w-full flex gap-[8px]">
                        <div className="w-[100px] font-500 text-14 text-[#262626]">{translate('discount')}</div>
                        <div className="text-14 font-400 text-[#262626]">{symbolCurrency} {convertCurrency(item.discount_amount, orderDetail?.currency, currentRates)}</div>
                      </div>

                      <div className="w-full flex gap-[8px]">
                        <div className="w-[100px] font-500 text-14 text-[#262626]">{translate('total')}</div>
                        <div className="text-14 font-400 text-[#262626]">{symbolCurrency} {convertCurrency(item.grand_total, orderDetail?.currency, currentRates)}</div>
                      </div>

                      <div className="w-full flex gap-[8px]">
                        <div className="w-[100px] font-500 text-14 text-[#262626]">{translate('used-code')}</div>
                        <div className="text-14 font-400 text-[#262626]">
                          {
                            orderDetail.coupons && orderDetail.coupons.length > 0 &&
                            orderDetail.coupons.map((coupon: any, index: number) => {
                              return (
                                <span key={`${coupon}_${index}`}>{coupon}</span>
                              )
                            })
                          }
                        </div>
                      </div>
                    </div>
                  )
                })
              }
            </div>
          }

          {
            (activeAttendants && orderDetail && (orderDetail.order_status === 'dat_hang_thanh_cong' || orderDetail.order_status === 'hoan_tien')) &&
            <div className={`${isMobile ? 'p-[16px]' : 'p-[24px]'} w-full bg-white rounded-[4px]`}>
              <div className="w-full">
                <div className="text-16 font-700 mb-[16px]">{translate('participant-information')}</div>

                {
                  !isMobile &&
                  <div className="body__item__search-group">
                    <div className="text-14 font-500 mb-[8px]">Email</div>

                    <div className="w-full flex gap-[12px] mb-[16px]">
                      <input type="text"
                        className="w-[250px] h-[40px] text-14 border border-[#c5ccd2] rounded-[4px]"
                        placeholder={translate('search-email-placeholder')}
                        onChange={(e: any) => setEmail(e.target.value)} />

                      {/* <Button
                        placeholder="Button search coupon"
                        className="w-[150px] h-[40px] rounded-[4px] flex items-center justify-center gap-[8px] border border-[#a6a1fd] text-14 uppercase text-brand-500"
                        onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                        onResizeCapture={() => { }}
                        onClick={() => setFilter(true)}>
                        <SearchIcon className="w-4 h-4 icon-search" />
                        <span>{translate('search')}</span>
                      </Button> */}

                      <NewButton
                        className="w-auto bg-white rounded-md px-3 text-brand-500 py-2 border-solid border-brand-500 border-[1px] hover:bg-white active:bg-white active:opacity-85"
                        onClick={() => { setFilter(true) }}
                      >
                        <SearchIcon className="w-3 h-4 text-brand-500 mr-2" /> {translate('search')}
                      </NewButton>
                    </div>

                    {
                      (attendants && attendants.length > 0) ?
                        <div className="grid grid-cols-4 gap-[12px]">
                          {
                            attendants.map((item: any, index: number) => {
                              return (
                                <div className="w-full rounded-[8px] border border-[#c5ccd2] p-[16px] flex justify-between gap-x-[8px] cursor-pointer" key={`${item.attendant_id}_${index}`}>
                                  <div className="size-[40px] rounded-full bg-[#f6f5fe] flex items-center justify-center" onClick={() => handleRedirectParticipant(item)}>
                                    <UserIcon className="w-4 h-4 text-[#a7b4fe]" />
                                  </div>

                                  <div className="flex flex-col w-[calc(100%-80px)]" onClick={() => handleRedirectParticipant(item)}>
                                    <div className="text-14 font-500 mb-[8px]">{item.first_name} {item.last_name}</div>
                                    <div className="text-10 mb-[20px]">{item.email}</div>

                                    {
                                      (item.is_sent_email_success !== "0") ?
                                        <div className="text-12 rounded-[16px] !font-500 px-[16px] py-[5px] text-center text-[#1d813f] bg-[#dcfce7]">
                                          {translate('sent-email')}
                                        </div>
                                        :
                                        <div className="text-12 rounded-[16px] !font-500 px-[16px] py-[5px] text-center text-[#9D0520] bg-[#FFCFDD]">
                                          {translate('not-sent-email')}
                                        </div>
                                    }

                                  </div>

                                  {
                                    (orderDetail && orderDetail.order_status && orderDetail.order_status === 'dat_hang_thanh_cong' && isHaveActionPermission("", "orders", "edit")) &&
                                    <MailIcon className="size-[16px] text-brand-500" onClick={() => handleSendEmailForParticipant(item.participant_id)} />
                                  }
                                </div>
                              )
                            })
                          }

                        </div>
                        :
                        <div className="w-full flex items-center justify-center py-[12px]">{translate('no-data')}</div>
                    }


                    {
                      paging &&
                      <div className="attendant-bottom">
                        {
                          paging.total_page > 1 &&
                          <Pagination
                            setPage={setPage}
                            paging={paging}
                            setFilter={setFilter}
                          />
                        }
                      </div>
                    }
                  </div>
                }

              </div>
            </div>
          }

          {
            (isMobile && activeAttendants && orderDetail && (orderDetail.order_status === 'dat_hang_thanh_cong' || orderDetail.order_status === 'hoan_tien')) &&
            <div className="w-full mt-[-16px]">
              <div className="border-t border-[#c5ccd2] bg-white p-[16px]">
                <div className="text-14 font-500">{translate('email')}</div>

                <div className="w-full flex items-center gap-x-[12px]">
                  <input type="text"
                    className="w-[200px] h-[40px] border border-[#c5ccd2] text-14 rounded-[4px]"
                    placeholder={translate('search-email-placeholder')}
                    onChange={(e: any) => setEmail(e.target.value)} />

                  {/* <Button
                    placeholder="Button search coupon"
                    className="flex-1 h-[40px] flex items-center justify-center border border-[#a6a1fd] text-14 text-brand-500 gap-x-[4px]"
                    onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                    onResizeCapture={() => { }}
                    onClick={() => setFilter(true)}>
                    <SearchIcon className="w-4 h-4 icon-search" />
                    <span>{translate('search')}</span>
                  </Button> */}

                  <NewButton
                    className="w-auto bg-white rounded-md px-3 text-brand-500 py-2 border-solid border-brand-500 border-[1px] hover:bg-white active:bg-white active:opacity-85"
                    onClick={() => { setFilter(true) }}
                  >
                    <SearchIcon className="w-3 h-4 text-brand-500 mr-2" /> {translate('search')}
                  </NewButton>
                </div>
              </div>

              {
                (attendants && attendants?.length > 0) ?
                  <div className="w-full my-[8px] gap-[8px] flex flex-wrap items-center justify-center">
                    {
                      attendants.map((item: any, index: number) => {
                        return (
                          <div className="w-[calc(100%-32px)] bg-white rounded-[4px] p-[16px] flex gap-x-[4px]" key={`${item.item_id}_${index}`}>
                            <div className="size-[40px] bg-[#f6f5fe] rounded-full flex items-center justify-center" onClick={() => handleRedirectParticipant(item)}>
                              <UserIcon className="w-4 h-4 text-brand-500" />
                            </div>

                            <div className="w-[calc(100%-64px)]" onClick={() => handleRedirectParticipant(item)}>
                              <div className="text-14 font-500">{item.first_name} {item.last_name}</div>
                              <div className="text-10 my-[8px]">{item.email}</div>

                              {
                                (item.is_sent_email_success !== "0") ?
                                  <div className="text-12 rounded-[16px] !font-500 px-[16px] py-[5px] text-center text-[#1d813f] bg-[#dcfce7] max-w-[130px]">
                                    {translate('sent-email')}
                                  </div>
                                  :
                                  <div className="text-12 rounded-[16px] !font-500 px-[16px] py-[5px] text-center text-[#9D0520] bg-[#FFCFDD] max-w-[130px]">
                                    {translate('not-sent-email')}
                                  </div>
                              }

                            </div>

                            {
                              (orderDetail && orderDetail.order_status && orderDetail.order_status === 'dat_hang_thanh_cong' && isHaveActionPermission("", "orders", "edit")) &&
                              <MailIcon className="size-[16px] text-brand-500" onClick={() => handleSendEmailForParticipant(item.participant_id)} />
                            }
                          </div>
                        )
                      })
                    }
                  </div>
                  :
                  <div className="w-full flex items-center justify-center py-[12px]">{translate('no-data')}</div>
              }

              {
                paging &&
                <div className="attendant-bottom">
                  {
                    paging.total_page > 1 &&
                    <Pagination
                      setPage={setPage}
                      paging={paging}
                      setFilter={setFilter}
                    />
                  }
                </div>
              }
            </div>
          }
        </div>
      }

      {
        loading &&
        <FullScreenLoading />
      }
    </div>
  )
}

const WPSOrderDetail = () => {
  const windowWidth = useWindowWidth();
  const [isMobile, setIsMobile] = useState(false);
  const { translate, getLocale } = useTranslation();
  const language = getLocale();
  const parse = useParse();
  const { params } = parse();
  const [orderID, setOrderID] = useState('');
  const [token, setToken] = useState('');

  useEffect(() => {
    const user = localStorage.getItem("USER");

    if (user) {
      const userData = JSON.parse(user);
      setToken(userData?.token);
    }
  }, []);

  useEffect(() => {
    if (params && params?.id && params?.id !== '') {
      setOrderID(params?.id);
    }
  }, [params]);

  useEffect(() => {
    if (windowWidth && windowWidth < 1024) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [windowWidth]);

  return (
    <div className="w-full">
      {
        (orderID !== '' && token !== '') &&
        <div className='w-full'>
          <OrderForm
            isMobile={isMobile}
            translate={translate}
            language={language}
            orderID={orderID}
            token={token} />
        </div>
      }

    </div>
  )
}

export default WPSOrderDetail;